import "flatpickr/dist/themes/light.css";
import { useContext, useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ClipboardDocumentCheckIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";

import { apiRequest } from "src/async/apiUtils";
import Button from "src/components/form/Button";
import { SiteContext } from "src/context/site";
import { WorkspaceContext } from "src/context/workspace";
import { AuthContext } from "src/context/auth";
import { objectToFormData } from "src/helpers";
import { classNames } from "src/helpers/classNames";
import useIframeResize from "src/hooks/useIframeResize";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import { useAsyncAction } from "src/hooks/useAsyncAction";
import FormResponse from "./FormResponse";
import AllResponses from "./AllResponses";
import { RenderQuestionField, validateCustomInputField } from "src/helpers/formHelper";

const FORM_VIEWS = {
  FORM: "form",
  RESPONSE: "response",
  ALL_RESPONSES: "allResponses",
};

const QUESTION_TYPES = {
  CHECKBOX: "checkbox",
  CHECKBOX_GRID: "checkbox_grid",
  RADIO_GRID: "radio_grid",
  FILE: "file",
  RICH_TEXT: "rich_text",
  QUESTION: "question",
  TITLE: "title",
};

export default function FormEmbed({ content }) {
  const [sectionIndex, setSectionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [formResponse, setFormResponse] = useState(null);
  const [formView, setFormView] = useState(FORM_VIEWS.FORM);
  const [formResponses, setFormResponses] = useState([]);
  const [showCustomTagModal, setShowCustomTagModal] = useState(false);

  const formContainerRef = useRef(null);

  const { details: workspaceDetails, menuType } = useContext(WorkspaceContext);
  const { site } = useContext(SiteContext);
  const { user } = useContext(AuthContext);

  const { maxWidth } = useIframeResize({ workspaceDetails, site });
  const { workspace_id, content_value } = useParams();
  const { group, page, form } = useUrlQueryParams();

  const scrollToTop = useCallback(() => {
    if (formContainerRef.current) {
      formContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const resetForm = useCallback(() => {
    setSectionIndex(0);
    initializeAnswers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    loading: formSubmitting,
    success: formSubmitSuccess,
    execute: submitForm,
  } = useAsyncAction({
    onSuccess: (res) => {
      const responseData = res?.data?.data;
      const isLastSection = content?.form?.sections?.length === sectionIndex + 1;

      if (isLastSection) {
        setFormResponse({
          message: res.data.message,
          ...responseData,
        });
        setFormView(FORM_VIEWS.RESPONSE);
        resetForm();
        scrollToTop();
      } else {
        setSectionIndex((prevIndex) => prevIndex + 1);
        scrollToTop();
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const { loading: responsesLoading, execute: getResponses } = useAsyncAction({
    onSuccess: (res) => {
      const responseData = res?.data?.data;
      setFormResponses(responseData);
    },
  });

  const handleAnswerChange = useCallback((content_id, key, value, question_type, option_id) => {
    setValidationErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[content_id];
      return newErrors;
    });
    setAnswers((prevAnswers) =>
      prevAnswers.map((item) => {
        if (item?.content_id?.toString() !== content_id?.toString()) {
          return item;
        }

        let newValue = value;

        if (question_type === QUESTION_TYPES.CHECKBOX && key === "value") {
          if (value === true) {
            newValue = [...(item?.value || []), option_id];
          } else {
            newValue = (item?.value || []).filter((val) => val !== option_id);
          }
        }

        if (question_type === QUESTION_TYPES.FILE) {
          newValue = [...(item?.value || []), ...(value || [])];
        }

        return {
          ...item,
          [key]: newValue,
        };
      }),
    );
  }, []);

  const getInitialValue = useCallback((questionType, contentItem) => {
    const gridTypes = [QUESTION_TYPES.CHECKBOX_GRID, QUESTION_TYPES.RADIO_GRID];
    const arrayTypes = [QUESTION_TYPES.CHECKBOX, QUESTION_TYPES.FILE, ...gridTypes];

    if (arrayTypes.includes(questionType)) {
      if (gridTypes.includes(questionType)) {
        return contentItem.rows?.map((row) => ({
          row_id: row._id,
          option_id: questionType === QUESTION_TYPES.RADIO_GRID ? "" : [],
        }));
      }
      return [];
    }

    return questionType === QUESTION_TYPES.RICH_TEXT ? contentItem.value : "";
  }, []);

  const initializeAnswers = useCallback(() => {
    const answersArray = [];

    if (content?.form?.sections?.length > 0) {
      content.form.sections.forEach((section) => {
        section.contents?.forEach((contentItem) => {
          if (contentItem?.type === QUESTION_TYPES.QUESTION) {
            const initialValue = getInitialValue(contentItem.question_type, contentItem);

            answersArray.push({
              content_id: contentItem._id,
              value: initialValue,
              other_value: "",
            });
          }
        });
      });
    }

    setAnswers(answersArray);
  }, [content?.form?.sections, getInitialValue]);

  const [validationErrors, setValidationErrors] = useState({});
  const questionRefs = useRef({});

  useEffect(() => {
    setValidationErrors({});
    scrollToTop();
  }, [sectionIndex, scrollToTop]);

  const handleFormSubmit = useCallback(() => {
    const currentSection = content?.form?.sections[sectionIndex];
    const errors = {};
    let firstErrorField = null;

    currentSection?.contents?.forEach((contentItem) => {
      if (contentItem?.type === "question") {
        const answer = answers.find((ans) => ans?.content_id?.toString() === contentItem._id?.toString());
        const validation = contentItem?.validation;

        if (validation?.required) {
          let isEmpty = false;

          if (contentItem.question_type === "checkbox" || contentItem.question_type === "file") {
            isEmpty = !answer?.value || answer.value.length === 0;
          } else if (contentItem.question_type === "checkbox_grid" || contentItem.question_type === "radio_grid") {
            isEmpty =
              !answer?.value ||
              answer.value.length === 0 ||
              answer.value.some((row) => {
                if (contentItem.question_type === "radio_grid") {
                  return !row.option_id;
                } else {
                  return !row.option_id || row.option_id.length === 0;
                }
              });
          } else if (contentItem.question_type === "select" && contentItem.multiple_select) {
            isEmpty = !answer?.value || answer.value.length === 0;
          } else {
            isEmpty = !answer?.value || (typeof answer.value === "string" && !answer.value.trim());
          }

          if (isEmpty) {
            errors[contentItem._id] = `${contentItem.name} is required`;
            if (!firstErrorField) {
              firstErrorField = contentItem._id;
            }
          }
        }

        if (contentItem.question_type === "input" && validation?.custom_validation && answer?.value && !errors[contentItem._id]) {
          const customError = validateCustomInputField(answer.value, validation, contentItem);
          if (customError) {
            errors[contentItem._id] = customError;
            if (!firstErrorField) {
              firstErrorField = contentItem._id;
            }
          }
        }
      }
    });

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0 && firstErrorField) {
      const errorElement = questionRefs.current[firstErrorField];
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      toast.error("Please fix all validation errors");
      return;
    }

    setValidationErrors({});

    submitForm(async () => {
      const payload = {
        answers,
        section_id: content?.form?.sections[sectionIndex]?._id,
      };
      const formData = objectToFormData(payload);

      return await apiRequest(
        "POST",
        `/portal/workspaces/:workspace_id/forms/:form_id`,
        {
          params: {
            workspace_id,
            form_id: content?.form?._id,
          },
          queries: {
            group,
            page: page,
            form: form || content_value,
          },
          body: formData,
        },
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
    });
  }, [submitForm, answers, content, sectionIndex, workspace_id, group, page, content_value, form]);

  const handleStartNewSubmission = useCallback(() => {
    setFormView(FORM_VIEWS.FORM);
    resetForm();
    scrollToTop();
  }, [resetForm, scrollToTop]);

  const handleShowAllResponses = useCallback(() => {
    setFormView(FORM_VIEWS.ALL_RESPONSES);
    fetchFormResponses();
    scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  const handleBackToForm = useCallback(() => {
    setFormView(FORM_VIEWS.FORM);
    scrollToTop();
  }, [scrollToTop]);

  const handlePreviousSection = useCallback(() => {
    setSectionIndex((prev) => prev - 1);
    scrollToTop();
  }, [scrollToTop]);

  const fetchFormResponses = useCallback(() => {
    getResponses(async () => {
      return await apiRequest("GET", `/portal/workspaces/:workspace_id/forms/:form_id/response`, {
        params: {
          workspace_id: content?.workspace,
          form_id: content?.form?._id,
        },
        queries: {
          group_id: content?.group_id,
          page_id: page ? content?.page_id : null,
        },
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.workspace, content?.form?._id, content?.group_id, content?.page_id]);

  useEffect(() => {
    initializeAnswers();
  }, [initializeAnswers]);

  useEffect(() => {
    setFormView(FORM_VIEWS.FORM);
    setFormResponse(null);
    setFormResponses([]);
    setSectionIndex(0);
    initializeAnswers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, content?.form?._id]);

  useEffect(() => {
    if (user?._id) {
      fetchFormResponses();
    }
  }, [fetchFormResponses, user?._id]);

  const renderPreviousSubmissionsButton = () => {
    if (formResponses?.length === 0 || sectionIndex > 0) return null;

    return (
      <button
        onClick={handleShowAllResponses}
        className="animate-fade-in hover:bg-highlightColor/90 absolute -top-6 right-0 flex h-12 items-center justify-center gap-x-1 rounded-t-md bg-highlightColor px-2 pb-5 text-xs text-white transition-colors hover:underline">
        <ClipboardDocumentCheckIcon className="h-4 w-4" />
        See your previous submissions
      </button>
    );
  };

  const renderFormContent = (section) => {
    const hasTitle = section?.contents?.some((item) => item?.type === QUESTION_TYPES.TITLE);
    let buttonRendered = false;

    return section?.contents?.map((contentItem, idx) => {
      const isFirstQuestion = contentItem?.type === QUESTION_TYPES.QUESTION && !hasTitle && !buttonRendered;
      const shouldShowButton = (contentItem?.type === QUESTION_TYPES.TITLE && !buttonRendered) || isFirstQuestion;

      if (shouldShowButton) buttonRendered = true;

      return (
        <div key={contentItem?._id}>
          {contentItem?.type === QUESTION_TYPES.TITLE && (
            <div className="relative">
              {shouldShowButton && renderPreviousSubmissionsButton()}
              <div className="relative z-[1] mt-3 rounded-lg border-l-4 border-highlightColor bg-white p-6 shadow">
                <h1 className="mb-2 text-2xl font-bold text-gray-800">{contentItem?.name}</h1>
                {contentItem?.description && <p className="leading-relaxed text-gray-600">{contentItem?.description}</p>}
              </div>
            </div>
          )}

          {contentItem?.type === QUESTION_TYPES.QUESTION && (
            <div
              className="relative"
              ref={(el) => (questionRefs.current[contentItem._id] = el)}>
              {shouldShowButton && renderPreviousSubmissionsButton()}
              <div className="relative mt-3 rounded-lg bg-white p-6 shadow">
                <RenderQuestionField
                  response={false}
                  content={contentItem}
                  answers={answers}
                  dateOfSigning={null}
                  onHandleChange={handleAnswerChange}
                  showSignatureModal={showSignatureModal}
                  setShowSignatureModal={setShowSignatureModal}
                  showCustomTagModal={showCustomTagModal}
                  setShowCustomTagModal={setShowCustomTagModal}
                  validationError={validationErrors[contentItem._id]}
                />
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  const renderNavigationButtons = (currentSection, index) => {
    const nonRichTextQuestions = currentSection.contents?.filter((contentItem) => contentItem.question_type !== QUESTION_TYPES.RICH_TEXT && contentItem?.type !== QUESTION_TYPES.TITLE && contentItem.type === QUESTION_TYPES.QUESTION);

    if (nonRichTextQuestions?.length === 0 && content.form.sections.length === 1) return null;

    const isLastSection = content.form.sections.length === index + 1;

    return (
      <div className="flex items-center justify-between">
        <div>
          {index > 0 && (
            <Button
              buttonFunction={handlePreviousSection}
              buttonIcon={ChevronDoubleLeftIcon}
              buttonIconPosition="left"
              buttonClasses="!bg-gray-500 hover:!bg-gray-600 !px-6"
              buttonLabel="Back"
            />
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button
            isDisable={formSubmitting}
            loading={formSubmitting}
            success={formSubmitSuccess}
            buttonIcon={isLastSection ? PaperAirplaneIcon : ChevronDoubleRightIcon}
            buttonIconPosition="right"
            buttonClasses="!bg-highlightColor hover:!bg-highlightColor/90 !px-8"
            buttonFunction={handleFormSubmit}
            buttonLabel={isLastSection ? (formSubmitting ? "Submitting..." : "Submit Form") : formSubmitting ? "Proceeding..." : "Next"}
          />
        </div>
      </div>
    );
  };

  const containerClasses = classNames("relative flex w-full flex-grow flex-col", menuType === "horizontal" || menuType === "vertical" ? "" : "");

  if (formView === FORM_VIEWS.ALL_RESPONSES) {
    return (
      <div className={containerClasses}>
        <div className="mx-auto flex w-full flex-grow flex-col bg-backgroundColor">
          <div
            ref={formContainerRef}
            style={{ maxWidth: `min(${maxWidth}, 800px)` }}
            className="mx-auto w-full px-4 py-8">
            <AllResponses
              formResponses={formResponses}
              form={content?.form}
              onBack={handleBackToForm}
              loading={responsesLoading}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <div className="mx-auto flex w-full flex-grow flex-col bg-backgroundColor">
        <div
          ref={formContainerRef}
          style={{ maxWidth: `min(${maxWidth}, 800px)` }}
          className="mx-auto w-full px-4 py-8">
          {formView === FORM_VIEWS.RESPONSE ? (
            <FormResponse
              response={formResponse}
              form={content?.form}
              onStartNewSubmission={handleStartNewSubmission}
              navigateToAll={() => {
                setFormView(FORM_VIEWS.ALL_RESPONSES);
                fetchFormResponses();
              }}
            />
          ) : (
            content?.form?.sections?.map(
              (section, index) =>
                sectionIndex === index && (
                  <div
                    key={section?._id}
                    className="space-y-4">
                    <div className="space-y-4">
                      {renderFormContent(section, index)}
                      {renderNavigationButtons(section, index)}
                    </div>
                  </div>
                ),
            )
          )}
        </div>
      </div>
    </div>
  );
}
